import { useState, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Plus } from 'lucide-react';
import { useInstructions, useUrlSearch } from '@/hooks';
import type { Instruction } from '@/types';
import { cn } from '@/lib/utils';
import { InstructionCard, InstructionSheet, INSTRUCTION_FILTERS, type FilterType } from '@/modules/instructions';

const InstructionCardSkeleton = () => (
	<Card className='h-[190px] flex flex-col'>
		<CardContent className='flex flex-col h-full'>
			{/* First Row: Name and Switch */}
			<div className='flex justify-between items-center gap-2 mb-3'>
				<Skeleton className='h-4 w-32' />
				<div className='flex items-center gap-2'>
					<Skeleton className='h-8 w-8 rounded' />
					<Separator orientation='vertical' className='h-4' />
					<Skeleton className='h-5 w-9 rounded-full' />
				</div>
			</div>
			<Separator />

			{/* Description */}
			<div className='flex-1 py-1.5 space-y-1.5'>
				<Skeleton className='h-3 w-full' />
				<Skeleton className='h-3 w-full' />
				<Skeleton className='h-3 w-3/4' />
			</div>

			{/* Tags */}
			<div className='flex gap-1'>
				<Skeleton className='h-7 w-24 rounded-full' />
				<Skeleton className='h-7 w-10 rounded-full' />
			</div>
		</CardContent>
	</Card>
);

export default function Instructions() {
	const { searchQuery, setSearchQuery } = useUrlSearch('q', '/training/instructions');
	const [activeFilter, setActiveFilter] = useState<FilterType>('all');
	const [dialogOpen, setDialogOpen] = useState(false);
	const [selectedInstructionId, setSelectedInstructionId] = useState<string | null>(null);
	const { data: instructions, isLoading, error } = useInstructions();

	const filteredInstructions = useMemo(() => {
		if (!instructions) return [];

		let filtered = instructions;

		// Apply filter type
		switch (activeFilter) {
			case 'global':
				filtered = instructions.filter((g) => !g.knowledgeBases || g.knowledgeBases.length === 0);
				break;
			case 'escalation':
				filtered = instructions.filter((g) => g.type === 'escalation');
				break;
			case 'all':
			default:
				filtered = instructions;
		}

		// Apply search query
		if (searchQuery.trim()) {
			const query = searchQuery.toLowerCase();
			filtered = filtered.filter((g) => g.name.toLowerCase().includes(query) || g.description.toLowerCase().includes(query));
		}

		// // Filter out shadow-only items when shadow mode is disabled
		// if (!shadowModeEnabled) {
		//   filtered = filtered.filter((instruction) => {
		//     const tags = instruction.tags || [];
		//     // Exclude items that ONLY have "shadow" environment (not items that have both "live" and "shadow")
		//     const hasOnlyShadow = tags.includes('shadow') && !tags.includes('live');
		//     return !hasOnlyShadow;
		//   });
		// }

		return filtered;
	}, [instructions, activeFilter, searchQuery]);

	// Look up the current instruction from the latest data
	const selectedInstruction = useMemo(() => {
		if (!selectedInstructionId || !instructions) return null;
		return instructions.find((i) => i.id === selectedInstructionId) || null;
	}, [selectedInstructionId, instructions]);

	const handleCardClick = (instruction: Instruction) => {
		setSelectedInstructionId(instruction.id);
		setDialogOpen(true);
	};

	const handleAddClick = () => {
		setSelectedInstructionId(null);
		setDialogOpen(true);
	};

	const handleDialogClose = (open: boolean) => {
		setDialogOpen(open);
		if (!open) {
			setSelectedInstructionId(null);
		}
	};

	return (
		<div className='flex gap-6 flex-col p-4'>
			{/* Header, Search and Filters Section */}
			<div className='flex flex-col gap-8'>
				<div className='flex justify-between items-start'>
					<h1 className='text-base font-semibold'>Instructions</h1>
					<div className='flex gap-2'>
						<SearchInput
							className='h-8 w-72'
							placeholder='Search'
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>

						<Button className='h-8 gap-1 flex text-xs' onClick={handleAddClick}>
							<Plus /> Create Instruction
						</Button>
					</div>
				</div>
				{/* Search Box */}

				{/* Filter Chips */}
				<div className='flex gap-2'>
					{INSTRUCTION_FILTERS.map((filter) => (
						<Badge
							key={filter.value}
							variant={activeFilter === filter.value ? 'default' : 'outline'}
							className={cn(
								'cursor-pointer px-3.5 py-1.5 text-xs text-gray-950  border-0 hover:bg-gray-300 hover:text-primary transition-colors',
								activeFilter === filter.value && 'bg-white text-primary border border-gray-400 drop-shadow-xs'
							)}
							onClick={() => setActiveFilter(filter.value)}
						>
							{filter.label}
						</Badge>
					))}
				</div>
			</div>

			{/* Instructions Grid */}
			<div>
				{isLoading && (
					<div className='grid grid-cols-3 gap-4'>
						{[1, 2, 3, 4, 5, 6].map((i) => (
							<InstructionCardSkeleton key={i} />
						))}
					</div>
				)}

				{error && <div className='text-center py-8 text-destructive'>Error loading instructions. Please try again.</div>}

				{!isLoading && !error && filteredInstructions.length === 0 && (
					<div className='text-center py-8 text-muted-foreground'>
						{searchQuery ? 'No instructions found matching your search.' : 'No instructions available.'}
					</div>
				)}

				{!isLoading && !error && filteredInstructions.length > 0 && (
					<div className='grid grid-cols-3 gap-4'>
						{filteredInstructions.map((instruction) => (
							<InstructionCard key={instruction.id} instruction={instruction} onClick={handleCardClick} />
						))}
					</div>
				)}
			</div>

			<InstructionSheet open={dialogOpen} onOpenChange={handleDialogClose} instruction={selectedInstruction} />
		</div>
	);
}
